import { PublicLayout } from '@/components/public/PublicLayout';
import { PublicSeo } from '@/components/public/PublicSeo';
import { motion } from '@/components/public/PublicMotion';
import { fadeInUp, staggerContainer } from '@/hooks/use-scroll-animation';
import { DemoRequestForm } from '@/components/public/DemoRequestForm';
import { CalendarCheck, Phone, MonitorPlay, Handshake, ShieldCheck, Lock } from 'lucide-react';
import { TRUST_ITEMS } from '@/components/public/trust-items';

const steps = [
  {
    number: '1',
    icon: Phone,
    title: 'Ti contattiamo',
    text: 'Entro 24 ore ti scriviamo o chiamiamo per fissare un momento comodo.',
  },
  {
    number: '2',
    icon: MonitorPlay,
    title: 'Demo su misura',
    text: 'Ti mostriamo Emovia nel concreto, adattato al tuo modo di lavorare.',
  },
  {
    number: '3',
    icon: Handshake,
    title: 'Nessun impegno',
    text: 'È una conversazione, non un contratto. Decidi tu se e quando procedere.',
  },
];

const RichiediDemo = () => {
  return (
    <PublicLayout>
      <PublicSeo
        title="Emovia | Richiedi una Demo"
        description="Prenota una demo gratuita di Emovia e scopri come organizzare il materiale clinico che emerge tra una seduta e l'altra."
        path="/richiedi-demo"
      />

      {/* SEZIONE A — Hero */}
      <section className="py-20 md:py-28 bg-blue-lighter/50">
        <div className="container">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-2xl mx-auto text-center"
          >
            <motion.div variants={fadeInUp} transition={{ duration: 0.5 }}>
              <div className="w-20 h-20 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-6">
                <CalendarCheck className="w-12 h-12 text-primary" />
              </div>
            </motion.div>

            <motion.h1
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="font-display text-4xl md:text-5xl font-bold text-primary mb-3"
            >
              Richiedi una demo
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="text-lg text-primary-dark"
            >
              Compilare richiede meno di 2 minuti. Ti contatteremo entro 24 ore.
            </motion.p>

            <motion.div
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="flex items-center justify-center gap-3 mt-6 flex-wrap"
            >
              {['Demo gratuita', 'Nessun impegno', 'Risposta in 24h'].map((badge) => (
                <span
                  key={badge}
                  className="rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-primary/80"
                >
                  {badge}
                </span>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* SEZIONE B — Steps + Form */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 xl:gap-16 items-start">
            {/* Colonna sinistra */}
            <div>
              <h2 className="font-display text-2xl font-bold text-primary mb-8">
                Cosa succede dopo
              </h2>

              <div className="space-y-6">
                {steps.map(({ number, icon: Icon, title, text }) => (
                  <div key={number} className="flex gap-4 items-start">
                    <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0">
                      <span className="text-primary-foreground font-bold text-sm">{number}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-4 h-4 text-primary" />
                        <span className="font-semibold text-foreground">{title}</span>
                      </div>
                      <p className="text-muted-foreground text-sm leading-relaxed">{text}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <div className="rounded-2xl bg-green-lighter border border-green/20 p-5 flex gap-3 items-start">
                  <ShieldCheck className="w-5 h-5 text-green-dark shrink-0 mt-0.5" />
                  <p className="text-sm text-green-dark">
                    I tuoi dati vengono usati solo per organizzare la demo. Nessuna newsletter,
                    nessun rivenditore.
                  </p>
                </div>
              </div>
            </div>

            {/* Colonna destra — Form */}
            <div className="rounded-2xl border border-primary/20 bg-white shadow-sm p-6 md:p-8">
              <p className="text-sm font-medium text-primary mb-6 flex items-center gap-1.5">
                <Lock className="w-4 h-4" />
                Dati protetti · GDPR compliant
              </p>

              <DemoRequestForm />

              <p className="text-xs text-muted-foreground text-center mt-3">
                Risponderemo entro 24 ore lavorative
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SEZIONE C — Trust bar */}
      <section className="py-8 bg-primary">
        <div className="container">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            {TRUST_ITEMS.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2">
                <Icon className="w-5 h-5 text-primary-foreground/80" />
                <span className="text-primary-foreground/90 text-sm font-medium">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default RichiediDemo;
